import { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { KINDS, ordersFor } from '../lib/verdict';
import { cn, qty } from '../lib/utils';

/* The one loud element in the app: after every scan, a full-width band
   says where this carton goes. It clears itself after a few seconds or on
   tap; the next scan replaces it. A part of a multi-box item says so: the
   count only moves once every part of the item is in. */
export default function VerdictSheet({ verdict, onClose, timeout = 4000, onTop = false }) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!verdict) return undefined;
    const t = setTimeout(() => close.current?.(), timeout);
    return () => clearTimeout(t);
  }, [verdict, timeout]);

  if (!verdict) return null;
  const { kind, line, code, n, units, dest, box, missing = [], fullSet, alsoOn = 0, chosen = false } = verdict;
  const part = box && missing.length > 0; // one part in, the item is not complete yet
  const meta = KINDS[kind] || KINDS.stock;
  const orders = kind === 'out' || kind === 'crossdock' || kind === 'hold' ? ordersFor(dest, kind) : [];
  const first = orders[0];

  return (
    <button
      type="button"
      onClick={onClose}
      className={cn(meta.tone, 'sheet-in fixed inset-x-0 mx-auto w-full max-w-lg text-left', onTop ? 'z-50' : 'z-30')}
      style={onTop ? { top: 'calc(8px + env(safe-area-inset-top))' } : { bottom: 'calc(72px + env(safe-area-inset-bottom))' }}
      aria-live="assertive"
    >
      <div className="mx-2 mb-2 overflow-hidden rounded-2xl shadow-2xl">
        <div className="flex items-end justify-between bg-tone px-4 pb-2 pt-3 text-bg">
          <span key={`${line.id}-${n}-${kind}`} className="font-cond stamp text-[58px] font-bold leading-[0.85] tracking-tight">{meta.word}</span>
          {part ? (
            <span className="font-cond pb-1 text-right text-[30px] font-bold leading-none">
              PART {box.index + 1}<span className="text-[20px] font-semibold opacity-80"> of {box.total}</span>
            </span>
          ) : (
            <span className="font-cond pb-1 text-[30px] font-bold leading-none">
              {qty(box ? (units ?? n) : n)}<span className="text-[20px] font-semibold opacity-80"> / {qty(line.to_receive)}</span>
            </span>
          )}
        </div>
        <div className="px-4 py-3" style={{ backgroundColor: 'hsl(var(--surface))' }}>
          <p className="clamp-2 text-[16px] font-semibold leading-tight">{line.name}</p>
          {line.fragile && (
            <p className="mt-1 inline-flex items-center gap-1 text-[14px] font-bold text-alert">
              <AlertTriangle className="h-4 w-4" /> Fragile
            </p>
          )}
          {(code || line.vendor_code) && (
            <p className="mt-0.5 font-mono text-[13px] tabular-nums text-ink2">
              {code && <>EAN {code}</>}
              {code && line.vendor_code && ' · '}
              {line.vendor_code}
            </p>
          )}
          {part && (
            <p className="mt-1.5 rounded-lg bg-hold-bg px-2.5 py-1.5 text-[14px] text-hold">
              <span className="font-semibold">Only a part of the item:</span> {box.label}, part {box.index + 1} of {box.total}. Still need{' '}
              <span className="font-semibold">{missing.slice(0, 3).join(', ')}</span>
              {missing.length > 3 && ` +${missing.length - 3}`} before it counts.
              <span className="block text-[13px] opacity-80">
                {qty(units ?? 0)} of {qty(line.to_receive)} complete
              </span>
            </p>
          )}
          {box && !part && (
            <p className="mt-1 text-[14px]">
              <span className="font-semibold">{box.label}</span>
              <span className="text-ink2"> · part {box.index + 1} of {box.total} · </span>
              <span className="font-semibold text-out">all {box.total} parts in, item {qty(n)} complete</span>
            </p>
          )}
          {fullSet && <p className="mt-1 text-[14px] text-ink2">Unit barcode scanned: counted as a full set of {line.boxes.length} boxes.</p>}
          {alsoOn > 0 && (
            <p className="mt-1 text-[13px] text-ink2">
              {chosen
                ? `Same EAN on ${alsoOn} other ${alsoOn === 1 ? 'product' : 'products'}. Counted on the one you chose.`
                : `Same EAN on ${alsoOn} other ${alsoOn === 1 ? 'line' : 'lines'} of this order. Counted here; tap the line to move it.`}
            </p>
          )}
          <p className="mt-1 text-[14px] text-ink2">
            {kind === 'out' && first && (
              <>
                Completes order <span className="font-semibold text-ink">{first.ref}</span>
                {first.customer && <> for {first.customer}</>}
                {first.arrived_with?.length > 0 && <> together with {first.arrived_with.slice(0, 2).join(', ')} from this delivery</>}
                {orders.length > 1 && <> (+{orders.length - 1} more)</>}
              </>
            )}
            {kind === 'hold' && first && (
              <>
                Set aside. Order <span className="font-semibold text-ink">{first.ref}</span>
                {first.customer && <> for {first.customer}</>} also needs <span className="text-ink">{first.waiting_on.slice(0, 2).join(', ')}</span>
                {first.waiting_on.length > 2 && ` +${first.waiting_on.length - 2}`} from this delivery, not scanned yet. Becomes OUT once it is, CROSSDOCK if it does not arrive.
              </>
            )}
            {kind === 'crossdock' && first && (
              <>
                Order <span className="font-semibold text-ink">{first.ref}</span>
                {first.customer && <> for {first.customer}</>}
                {first.waiting_on?.length > 0 && (
                  <>
                    {' '}still waits on <span className="text-ink">{first.waiting_on.slice(0, 2).join(', ')}</span>
                    {first.waiting_on.length > 2 && ` +${first.waiting_on.length - 2}`}
                  </>
                )}
              </>
            )}
            {kind === 'stock' && 'No customer waiting. Put it away.'}
            {kind === 'over' && `Already ${qty(line.to_receive)} expected. Check the carton or count it as extra.`}
            {(kind === 'out' || kind === 'crossdock' || kind === 'hold') && !first && meta.title}
          </p>
        </div>
      </div>
    </button>
  );
}
